import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import _ from 'lodash'
import moment from 'momentExt'
import { BiX, BiChart, BiTable, BiCard, BiChevronLeft, BiChevronRight, BiDownload } from 'react-icons/bi'
import { FiExternalLink } from 'react-icons/fi'
import { reportsUrl } from 'urls'
import { getAxiosDefaultConfig, convertToDateRange } from 'utils'
import { defaultDashboardConfig } from 'modules/reportConfigs'
import DatePicker from 'sharedComponents/DatePicker'
import AIInsightCards, { hasInsightCards } from './AIInsightCards'
import AIChart, { buildChartElements } from './AIChart'
import ReportDataTable, { renderReportTable } from './ReportDataTable'
import BreakdownReportView from './BreakdownReportView'

export interface AgentReportPanelTarget {
  reportId: string
  title?: string
  reportType?: string
}

interface AgentReportPanelProps extends AgentReportPanelTarget {
  onClose: () => void
  /** Hide the close button (the mobile sheet supplies its own). */
  hideClose?: boolean
}

type Tab = 'insights' | 'chart' | 'table'

/** Spinning ring (tailwind's animate-spin, same as the rest of the app). */
const Spinner: React.FC<{ size?: number }> = ({ size = 32 }) => (
  <div
    role="status"
    aria-label="Loading"
    className="tw-animate-spin tw-rounded-full tw-border-2 tw-border-gray-200 tw-border-t-blue-600"
    style={{ width: size, height: size }}
  />
)

/** Renderers can throw on unexpected report shapes; never let that take the chat down with it. */
class PreviewBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  componentDidCatch(err: unknown) {
    console.error('[AgentReportPanel] preview render failed', err)
  }
  render() {
    return this.state.failed ? (
      <div className="tw-text-sm tw-text-gray-500">This report couldn't be displayed here. Use "Open in Reports" to view it.</div>
    ) : (
      this.props.children
    )
  }
}

/** Report types with no preview renderer yet. */
const PREVIEW_UNSUPPORTED_TYPES = new Set(['categoryInventory'])

const DATE_FORMAT = 'YYYY-MM-DD'
const DATE_TYPE = 'processed_at'
const PICKER_TYPE = 'agent-report-panel'
const PER_PAGE_OPTIONS = [25, 50, 100]
/** Reports rendered as a single chart/series rather than a list of rows: no paging. */
/** Results that are a facet object / expandable tree rather than rows: the server can't export these yet. */
const EXPORT_UNSUPPORTED_TYPES = new Set(['charts', 'categoryInventory'])
const UNPAGED_TYPES = new Set(['charts', 'trends', 'yearComparison', 'trafficTrends', 'categoryInventory'])

/** Row count for paging. Same rule the Reports page uses for its pager. */
const totalRecordsFor = (reportData: any, searchType: string): number => {
  const data = _.get(reportData, 'data') || {}
  const stats = data.stats || {}
  return Math.max(
    stats[`total_${searchType}`] || 0,
    searchType === 'productTrends' ? stats.total_products || 0 : 0,
    data.numFound || 0,
    data.totalResults || 0,
  )
}

/** Pivot Grouping nests facets up to 3 levels, so it opens on a week; everything else on 30 days. */
export const defaultRangeDaysFor = (reportType?: string) => (reportType === 'customOrderBases' ? 7 : 30)

export const defaultRange = (reportType?: string) => ({
  start: moment().subtract(defaultRangeDaysFor(reportType) - 1, 'days').format(DATE_FORMAT),
  end: moment().format(DATE_FORMAT),
})

/**
 * Read-only preview of a saved report inside the AI chat. Only the date range is editable here;
 * every other setting lives on the Reports page (linked from the header). Deliberately does not use the
 * global Redux date filters or the Reports page component, so previewing never changes the user's own views.
 */
const AgentReportPanel: React.FC<AgentReportPanelProps> = ({ reportId, title, reportType, onClose, hideClose }) => {
  const [range, setRange] = useState(() => defaultRange(reportType))
  const [reportData, setReportData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('table')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(PER_PAGE_OPTIONS[0])
  const requestId = useRef(0)
  const [exporting, setExporting] = useState<'csv' | 'pdf' | null>(null)
  const [exportNote, setExportNote] = useState<{ kind: 'error' | 'info'; text: string } | null>(null)

  const load = useCallback(async () => {
    const current = ++requestId.current
    setLoading(true)
    setError(null)
    try {
      const res = await axios.post(
        `${reportsUrl}/${reportId}`,
        {
          params: {
            date_type: DATE_TYPE,
            start_date: range.start,
            end_date: range.end,
            timestamp: Date.now(),
            rows: perPage,
            start: (page - 1) * perPage,
          },
        },
        getAxiosDefaultConfig(),
      )
      if (current !== requestId.current) return
      if (!res.data || !res.data.report) {
        setReportData(null)
        setError('This report could not be found, or is no longer available to you.')
        return
      }
      const { query, timeCost, report, ...rest } = res.data
      if (!_.get(report, ['config', 'config'])) _.set(report, ['config', 'config'], defaultDashboardConfig)
      const searchType = report.search_type
      const data = { ...rest }
      // UTM reports come back under "marketing"; the table/chart renderers look up data[search_type].
      if (!data[searchType] && data.marketing && String(searchType).startsWith('utm')) data[searchType] = data.marketing
      setReportData({ ...report, data })
    } catch (err: any) {
      if (current !== requestId.current) return
      console.error(err)
      setReportData(null)
      setError(err?.response?.data?.message || 'Could not load this report. Try a different date range.')
    } finally {
      if (current === requestId.current) setLoading(false)
    }
  }, [reportId, range.start, range.end, page, perPage])

  useEffect(() => {
    load()
  }, [load])

  const onSetDateRange = useCallback(({ dateRange }: { dateRange: { start: string; end: string } }) => {
    setPage(1)
    setExportNote(null)
    setRange({ start: dateRange.start, end: dateRange.end })
  }, [])

  const searchType: string = reportData?.search_type || reportType || ''

  /** Export the whole report for the selected date range (not just the current page) and download it. */
  const exportReport = useCallback(async (format: 'csv' | 'pdf') => {
    setExporting(format)
    setExportNote(null)
    try {
      const res = await axios.post(
        `${reportsUrl}/${reportId}/export`,
        { format, start_date: range.start, end_date: range.end, date_type: DATE_TYPE },
        getAxiosDefaultConfig(),
      )
      const out = res.data
      if (!out) {
        setExportNote({ kind: 'error', text: 'This report could not be found, or is no longer available to you.' })
        return
      }
      if (!out.data) {
        setExportNote({ kind: 'info', text: 'No data in this date range, so there is nothing to export.' })
        return
      }
      const bytes = Uint8Array.from(atob(out.data), (c) => c.charCodeAt(0))
      const url = window.URL.createObjectURL(new Blob([bytes], { type: out.mime }))
      const a = document.createElement('a')
      a.href = url
      a.download = out.filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      // Deferred: revoking right away breaks the download on iOS Safari.
      setTimeout(() => window.URL.revokeObjectURL(url), 10000)
      if (out.truncated) {
        setExportNote({
          kind: 'info',
          text: format === 'pdf'
            ? `PDF shows the first rows only (${Number(out.rowCount).toLocaleString()} total). Use CSV for everything.`
            : `Exported the first ${Number(out.rowCount).toLocaleString()} rows (the export limit). Narrow the date range for the rest.`,
        })
      }
    } catch (err: any) {
      console.error(err)
      setExportNote({ kind: 'error', text: err?.response?.data?.error || 'Export failed. Please try again.' })
    } finally {
      setExporting(null)
    }
  }, [reportId, range.start, range.end])
  const heading = reportData?.title || title || 'Report'
  const isBreakdown = searchType === 'charts'
  const allTabs: Array<{ key: Tab; label: string; icon: React.ReactNode }> = [
    { key: 'table', label: 'Table', icon: <BiTable size={16} /> },
    { key: 'chart', label: 'Chart', icon: <BiChart size={16} /> },
    { key: 'insights', label: 'Summary', icon: <BiCard size={16} /> },
  ]
  // Only offer the views this report actually has data for (a table-only report shows just Table, etc.).
  const available = useMemo<Record<Tab, boolean>>(() => {
    if (!reportData || isBreakdown || PREVIEW_UNSUPPORTED_TYPES.has(searchType)) return { table: false, chart: false, insights: false }
    const safe = (fn: () => boolean) => {
      try {
        return fn()
      } catch {
        return false
      }
    }
    return {
      table: safe(() => renderReportTable(reportData, searchType, perPage, { respectColumnConfig: true }) != null),
      chart: safe(() => buildChartElements(reportData, searchType).length > 0),
      insights: safe(() => hasInsightCards(reportData, searchType)),
    }
  }, [reportData, searchType, perPage, isBreakdown])
  const tabs = allTabs.filter((t) => available[t.key])
  const shownTab: Tab | null = tabs.some((t) => t.key === activeTab) ? activeTab : tabs[0]?.key ?? null
  const total = reportData ? totalRecordsFor(reportData, searchType) : 0
  const maxPage = Math.max(1, Math.ceil(total / perPage))
  const showPager = !!reportData && !UNPAGED_TYPES.has(searchType) && total > 0 && shownTab === 'table' && total > PER_PAGE_OPTIONS[0]
  const slug = String(heading).replace(/ /g, '-').replace(/-+/g, '-')

  return (
    <div className="tw-flex tw-flex-col tw-h-full tw-bg-white tw-border-l tw-border-gray-200">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-4 tw-py-3 tw-border-b tw-border-gray-200 tw-bg-gray-50">
        <div className="tw-min-w-0">
          <h3 className="tw-font-semibold tw-text-gray-800 tw-text-sm tw-truncate" title={heading}>{heading}</h3>
          {reportData?.type && <div className="tw-text-xs tw-text-gray-500">{reportData.type}</div>}
        </div>
        <div className="tw-flex tw-items-center tw-gap-1 tw-shrink-0">
          <a
            href={`/reports/${reportId}/${encodeURIComponent(slug)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-blue-600 hover:tw-underline tw-px-2 tw-py-1"
          >
            <FiExternalLink size={14} />
            <span>Open in Reports</span>
          </a>
          {!hideClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close report preview"
              className="tw-p-1 tw-rounded tw-text-gray-500 hover:tw-bg-gray-200"
            >
              <BiX size={20} />
            </button>
          )}
        </div>
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-border-b tw-border-gray-200">
        <DatePicker
          selectedType={PICKER_TYPE}
          dateRange={convertToDateRange({ startDate: range.start, endDate: range.end })}
          setDateRange={onSetDateRange}
        />
        {!!reportData && !EXPORT_UNSUPPORTED_TYPES.has(searchType) && (
          <div className="tw-flex tw-items-center tw-gap-1 tw-ml-auto">
            {(['csv', 'pdf'] as const).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => exportReport(f)}
                disabled={loading || !!exporting}
                title={`Download ${f.toUpperCase()} for the selected date range`}
                className="tw-flex tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                <BiDownload size={14} />
                <span>{exporting === f ? 'Exporting…' : f.toUpperCase()}</span>
              </button>
            ))}
          </div>
        )}
        <span className="tw-w-full tw-text-xs tw-text-gray-400">Other settings can be changed in the Reports tab.</span>
        {exportNote && (
          <span className={`tw-w-full tw-text-xs ${exportNote.kind === 'error' ? 'tw-text-red-600' : 'tw-text-gray-600'}`} role="status">
            {exportNote.text}
          </span>
        )}
      </div>

      {!isBreakdown && tabs.length > 0 && (
        <div className="tw-flex tw-border-b tw-border-gray-200">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-border-b-2 ${
                shownTab === t.key ? 'tw-border-blue-600 tw-text-blue-600' : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700'
              }`}
            >
              {t.icon}
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      )}

      <div className="tw-relative tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-p-4">
        {/* Refetch (date range / page change): keep the old data dimmed and spin on top of it */}
        {loading && reportData && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-6 tw-z-10 tw-flex tw-justify-center">
            <div className="tw-rounded-full tw-bg-white tw-p-2 tw-shadow-md tw-border tw-border-gray-200">
              <Spinner size={24} />
            </div>
          </div>
        )}
        {loading && !reportData ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-gap-3 tw-text-gray-500 tw-text-sm">
            <Spinner />
            <span>Loading report…</span>
          </div>
        ) : error ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-gap-2 tw-text-sm tw-text-gray-500">
            <span>{error}</span>
            <button type="button" onClick={load} className="tw-text-blue-600 hover:tw-underline">Try again</button>
          </div>
        ) : reportData ? (
          <div className={loading ? 'tw-opacity-50 tw-transition-opacity' : ''}>
            {PREVIEW_UNSUPPORTED_TYPES.has(searchType) ? (
              <div className="tw-text-sm tw-text-gray-500">This report type can't be previewed here yet. Use "Open in Reports" to view it.</div>
            ) : (
            <PreviewBoundary key={`${reportId}-${searchType}-${shownTab}`}>
            {isBreakdown ? (
              <BreakdownReportView reportData={reportData} />
            ) : (
              <>
                {shownTab === 'chart' && <AIChart reportData={reportData} reportType={searchType} />}
                {shownTab === 'insights' && <AIInsightCards reportData={reportData} reportType={searchType} />}
                {shownTab === 'table' && <ReportDataTable reportType={searchType} maxRows={perPage} reportData={reportData} respectColumnConfig />}
                {shownTab === null && (
                  <div className="tw-text-sm tw-text-gray-500">No data to display for this date range. Try a wider range.</div>
                )}
              </>
            )}
            </PreviewBoundary>
            )}
          </div>
        ) : null}
      </div>

      {showPager && !isBreakdown && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-px-4 tw-py-2 tw-border-t tw-border-gray-200 tw-bg-gray-50 tw-text-sm tw-text-gray-600">
          <label className="tw-flex tw-items-center tw-gap-1.5">
            <span className="tw-text-xs tw-text-gray-500">Rows</span>
            <select
              value={perPage}
              onChange={(e) => { setPage(1); setPerPage(Number(e.target.value)) }}
              className="tw-border tw-border-gray-300 tw-rounded tw-px-1.5 tw-py-0.5 tw-text-sm tw-bg-white"
              aria-label="Rows per page"
            >
              {PER_PAGE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <span className="tw-text-xs tw-text-gray-500">
            {((page - 1) * perPage + 1).toLocaleString()}–{Math.min(page * perPage, total).toLocaleString()} of {total.toLocaleString()}
          </span>
          <div className="tw-flex tw-items-center tw-gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              aria-label="Previous page"
              className="tw-p-1 tw-rounded hover:tw-bg-gray-200 disabled:tw-opacity-30 disabled:tw-cursor-not-allowed"
            >
              <BiChevronLeft size={20} />
            </button>
            <span className="tw-text-xs tw-min-w-[4.5rem] tw-text-center">Page {page} of {maxPage}</span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(maxPage, p + 1))}
              disabled={page >= maxPage || loading}
              aria-label="Next page"
              className="tw-p-1 tw-rounded hover:tw-bg-gray-200 disabled:tw-opacity-30 disabled:tw-cursor-not-allowed"
            >
              <BiChevronRight size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default AgentReportPanel
